import React, {useCallback, useMemo, useState} from 'react';
import {FlatList, RefreshControl, StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchMyAttendance} from '@/entities/me/api';
import type {AttendanceDayRecord} from '@/entities/me/types';
import {ApiError} from '@/shared/api';
import {Avatar} from '@/shared/ui/avatar';
import {IconButton} from '@/shared/ui/button';
import {EmptyState} from '@/shared/ui/empty-state';
import {Explain, type Definition} from '@/shared/ui/explain';
import {Skeleton, SkeletonList} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';
import {SummaryTiles} from './SummaryTiles';
import {DayRow} from './DayRow';

/**
 * "Моя посещаемость" — the employee's own month.
 *
 * A month is a {year, month} pair, not a Date: the query key is a plain "YYYY-MM"
 * string, and stepping never lands on a future month (there is no attendance to
 * report for days that have not happened). The report is self-scoped — a 404 means
 * "you are not linked to a personnel record in this company", which is a state to
 * show, not an error to swallow.
 *
 * The same rule holds one level down, and used not to: the CURRENT month was asked for
 * whole, so the engine called every remaining working day an absence and this screen
 * painted them red. `fetchMyAttendance` now cuts the request off at today
 * (`requestPeriod`), so the month in view ends on the day it has actually reached — the
 * grid simply has fewer cells, and the counters count only days that happened.
 *
 * The month switcher is pinned above the scroll rather than riding inside it: stepping
 * back three months and then scrolling should not hide the control that got you there.
 */
type Ym = {year: number; month: number}; // month is 1..12

function toKey(ym: Ym): string {
  return `${ym.year}-${String(ym.month).padStart(2, '0')}`;
}

function step(ym: Ym, delta: number): Ym {
  const zero = ym.year * 12 + (ym.month - 1) + delta;
  return {year: Math.floor(zero / 12), month: (zero % 12) + 1};
}

function isAfter(a: Ym, b: Ym): boolean {
  return a.year * 12 + a.month > b.year * 12 + b.month;
}

const styles = StyleSheet.create({
  frame: {flex: 1},
  switcher: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing(3),
    paddingVertical: spacing(2),
    paddingHorizontal: spacing(2),
    marginBottom: spacing(3),
    borderRadius: radii.md,
  },
  // The left chevron is the right one, turned. One glyph, two directions.
  flip: {transform: [{rotate: '180deg'}]},
  monthLabel: {flex: 1, textAlign: 'center'},
  list: {gap: spacing(2), paddingBottom: spacing(8), flexGrow: 1},
  listEmpty: {justifyContent: 'center'},
  headerBlock: {gap: spacing(4), paddingBottom: spacing(4)},
  whoRow: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  who: {flex: 1, gap: 2},
  skeletonHead: {gap: spacing(3), paddingBottom: spacing(4)},
});

export function AttendanceMonth({companyId}: {companyId: string}) {
  const {t} = useTranslation();
  const p = useTheme();

  // "Now" is read once on mount — the ceiling for forward stepping and the initial month.
  const now = useMemo(() => {
    const d = new Date();
    return {year: d.getFullYear(), month: d.getMonth() + 1} as Ym;
  }, []);
  const [ym, setYm] = useState<Ym>(now);
  const monthKey = toKey(ym);
  const atCeiling = !isAfter(now, ym); // now is not after ym ⇒ ym is current or (guarded) future

  const query = useQuery({
    queryKey: ['me-attendance', companyId, monthKey],
    queryFn: () => fetchMyAttendance(companyId, {kind: 'month', month: monthKey}),
  });

  const goPrev = useCallback(() => setYm(v => step(v, -1)), []);
  const goNext = useCallback(
    () => setYm(v => (isAfter(now, v) ? step(v, 1) : v)),
    [now],
  );

  const renderItem = useCallback(
    ({item}: {item: AttendanceDayRecord}) => (
      <DayRow day={item} companyId={companyId} />
    ),
    [companyId],
  );

  const monthLabel = `${t(`attendance.month.${ym.month}`)} ${ym.year}`;

  /**
   * The same glossary the manager-side reports carry, narrowed to what an employee sees
   * of their own month. The words are the engine's — «Вне графика», «Неполный день» —
   * and they are no more self-explanatory here than they are on a company board.
   */
  const definitions: Definition[] = [
    'worked',
    'present',
    'absent',
    'onTime',
    'late',
    'earlyArrival',
    'earlyLeave',
    'overtime',
    'incomplete',
    'unscheduled',
  ].map(id => ({
    term: t(`reports.def.${id}.term`),
    meaning: t(`reports.def.${id}.meaning`),
    formula: t(`reports.def.${id}.formula`, {defaultValue: ''}) || undefined,
  }));
  const notLinked =
    query.error instanceof ApiError && query.error.status === 404;

  const switcher = (
    <View style={[styles.switcher, {backgroundColor: p.card}]}>
      <IconButton
        icon="chevron"
        tone="ghost"
        label={t('attendance.prevMonth')}
        onPress={goPrev}
        style={styles.flip}
      />
      <AppText variant="heading" style={styles.monthLabel}>
        {monthLabel}
      </AppText>
      <IconButton
        icon="chevron"
        tone="ghost"
        label={t('attendance.nextMonth')}
        onPress={goNext}
        disabled={atCeiling}
      />
    </View>
  );

  let content: React.ReactNode;

  if (query.isPending) {
    content = (
      <View>
        <View style={styles.skeletonHead}>
          <Skeleton height={82} radius={radii.lg} />
          <Skeleton height={64} radius={radii.md} />
        </View>
        <SkeletonList count={6} avatar={false} />
      </View>
    );
  } else if (notLinked) {
    // Not an error: this account simply has no personnel record in this company.
    content = <EmptyState icon="attendance" title={t('attendance.notLinked')} />;
  } else if (query.isError) {
    content = (
      <EmptyState
        tone="error"
        title={t('attendance.error')}
        actionLabel={t('actions.retry')}
        onAction={() => {
          void query.refetch();
        }}
      />
    );
  } else {
    const report = query.data;
    const listHeader = (
      <View style={styles.headerBlock}>
        <View style={styles.whoRow}>
          {/* Своё лицо — из отчёта, а не отдельным запросом: `/me/attendance` уже
              вернул личное дело, а маршрута аватара в семье `/me` нет вовсе, и звать
              `/control/personnel/{id}/avatar` сотруднику нечем — права `personnel` у
              него нет. Пусто — инициалы, как и везде. */}
          <Avatar
            name={report.personnel.full_name}
            base64={report.personnel.avatar_base64 || null}
            size={44}
          />
          <View style={styles.who}>
            <AppText variant="title">{report.personnel.full_name}</AppText>
            {report.personnel.position ? (
              <AppText variant="caption">{report.personnel.position}</AppText>
            ) : null}
          </View>
          <Explain
            testID="explain-my-attendance"
            title={t('reports.glossary')}
            subtitle={t('reports.glossaryHint')}
            definitions={definitions}
          />
        </View>
        <SummaryTiles summary={report.summary} />
      </View>
    );
    content = (
      <FlatList
        testID="my-attendance"
        data={report.days}
        keyExtractor={d => d.date}
        renderItem={renderItem}
        ListHeaderComponent={listHeader}
        showsVerticalScrollIndicator
        contentContainerStyle={[
          styles.list,
          report.days.length === 0 && styles.listEmpty,
        ]}
        ListEmptyComponent={
          <EmptyState icon="attendance" title={t('attendance.emptyMonth')} />
        }
        refreshControl={
          <RefreshControl
            refreshing={query.isRefetching}
            onRefresh={() => {
              void query.refetch();
            }}
            tintColor={p.primary}
            colors={[p.primary]}
          />
        }
      />
    );
  }

  // Раму держит страница-хозяин (`Page width="form"`): месяц одного человека — это
  // ДОКУМЕНТ, а не доска, и читаемая колонка нужна ему, чтобы четыре счётчика дней не
  // растянулись каждый в 400pt коробку с одной цифрой. Своего `Screen` здесь нет —
  // второй фрейм платил бы отступы и безопасную зону дважды.
  return (
    <View style={styles.frame} testID="employee-attendance">
      {switcher}
      {content}
    </View>
  );
}
